'use client';

import React, { useState } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import CTABanner from '@/components/CTABanner';

interface FormState {
  companyName: string;
  contactName: string;
  email: string;
  phone: string;
  partnerType: string;
  description: string;
  website: string;
}

// Toast component
function Toast({ message, type, onClose }: { message: string; type: 'success' | 'error'; onClose: () => void }) {
  return (
    <div style={{
      position: 'fixed', bottom: '24px', right: '24px', zIndex: 9999,
      background: type === 'success' ? '#00b28a' : '#e53e3e',
      color: '#ffffff', borderRadius: '12px', padding: '14px 20px',
      fontFamily: 'DM Sans, sans-serif', fontSize: '0.9rem', fontWeight: 600,
      boxShadow: '0 8px 24px rgba(0,0,0,0.15)', display: 'flex', alignItems: 'center', gap: '10px',
      maxWidth: '360px', animation: 'slideUp 0.3s ease',
    }}>
      <span>{type === 'success' ? '✓' : '⚠'}</span>
      <span style={{ flex: 1 }}>{message}</span>
      <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.8)', cursor: 'pointer', fontSize: '1rem', padding: 0 }}>×</button>
    </div>
  );
}

const partnerBenefits = [
  { icon: '🤝', title: 'Qualified Borrowers', description: 'Access pre-screened loan applicants who have already expressed intent to borrow. Our matching algorithm ensures high-quality leads.' },
  { icon: '📊', title: 'Real-Time Dashboard', description: 'Manage applications, track conversions, and monitor performance through our dedicated partner portal with live analytics.' },
  { icon: '⚡', title: 'Fast Integration', description: 'Our API-first approach means you can integrate with Lendo in days, not months. Full documentation and dedicated support included.' },
  { icon: '🔒', title: 'Compliance Ready', description: 'We handle KYC/AML verification, document collection, and identity checks so you can focus on lending decisions.' },
  { icon: '💰', title: 'Competitive Economics', description: 'Transparent, performance-based pricing with no hidden fees. Pay only for funded loans, not leads.' },
  { icon: '📈', title: 'Growth Support', description: 'Dedicated account managers, co-marketing opportunities, and access to Lendo\'s growing borrower base of 50,000+ applicants.' },
];

const partnerTypes = [
  { icon: '🏦', title: 'Banks & Credit Unions', description: 'Expand your small business lending portfolio with qualified applicants.' },
  { icon: '💼', title: 'Alternative Lenders', description: 'Access a steady stream of pre-qualified borrowers for your loan products.' },
  { icon: '🔗', title: 'Referral Partners', description: 'Earn commissions by referring small business owners to Lendo.' },
  { icon: '🛠️', title: 'Technology Partners', description: 'Integrate your fintech solution with our platform to enhance borrower experience.' },
];

export default function PartnerPage() {
  const [form, setForm] = useState<FormState>({ companyName: '', contactName: '', email: '', phone: '', partnerType: '', description: '', website: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!form.companyName.trim()) newErrors.companyName = 'Company name is required';
    if (!form.contactName.trim()) newErrors.contactName = 'Contact name is required';
    if (!form.email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) newErrors.email = 'Please enter a valid email';
    if (!form.partnerType) newErrors.partnerType = 'Please select a partner type';
    if (!form.description.trim()) newErrors.description = 'Please describe your partnership interest';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    await new Promise((resolve) => setTimeout(resolve, 1200));
    setSubmitting(false);
    setSubmitted(true);
    showToast('Partnership inquiry submitted! We\'ll be in touch within 2 business days.', 'success');
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '12px 16px',
    border: '1.5px solid #e2e8f0', borderRadius: '10px',
    fontFamily: 'DM Sans, sans-serif', fontSize: '0.95rem', color: '#1a2b4a',
    outline: 'none', transition: 'border-color 0.2s', boxSizing: 'border-box', background: '#fafbfc',
  };

  return (
    <>
      <Navbar />
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      <main style={{ paddingTop: '88px', minHeight: '100vh', background: '#f7f9fc' }}>

        {/* Hero */}
        <div style={{ background: 'linear-gradient(135deg, #1a2b4a 0%, #0d1f38 100%)', padding: '80px 24px' }}>
          <div style={{ maxWidth: '1100px', margin: '0 auto', textAlign: 'center' }}>
            <div style={{ display: 'inline-block', background: 'rgba(0,178,138,0.15)', border: '1px solid rgba(0,178,138,0.3)', borderRadius: '50px', padding: '6px 16px', marginBottom: '20px' }}>
              <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.82rem', fontWeight: 600, color: '#00b28a' }}>🤝 Partnership Program</span>
            </div>
            <h1 style={{ fontFamily: 'Merriweather, serif', fontWeight: 900, fontSize: '2.6rem', color: '#ffffff', margin: '0 0 16px', lineHeight: 1.2 }}>
              Grow Together with Lendo
            </h1>
            <p style={{ fontFamily: 'DM Sans, sans-serif', color: 'rgba(255,255,255,0.75)', fontSize: '1.05rem', maxWidth: '580px', margin: '0 auto 32px', lineHeight: 1.7 }}>
              Join our network of lending partners and gain access to thousands of pre-qualified small business borrowers. Let&apos;s build something great together.
            </p>
            <div style={{ display: 'flex', gap: '32px', justifyContent: 'center', flexWrap: 'wrap' }}>
              {[{ value: '50K+', label: 'Borrowers' }, { value: '$2B+', label: 'Funded' }, { value: '200+', label: 'Partners' }, { value: '98%', label: 'Satisfaction' }].map((stat) => (
                <div key={stat.label} style={{ textAlign: 'center' }}>
                  <p style={{ fontFamily: 'Merriweather, serif', fontWeight: 900, fontSize: '1.8rem', color: '#00b28a', margin: '0 0 4px' }}>{stat.value}</p>
                  <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.82rem', color: 'rgba(255,255,255,0.6)', margin: 0 }}>{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Partner Types */}
        <div style={{ background: '#ffffff', padding: '80px 24px' }}>
          <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
            <h2 style={{ fontFamily: 'Merriweather, serif', fontWeight: 800, fontSize: '1.8rem', color: '#1a2b4a', textAlign: 'center', margin: '0 0 12px' }}>Who We Partner With</h2>
            <p style={{ fontFamily: 'DM Sans, sans-serif', color: '#718096', textAlign: 'center', fontSize: '0.95rem', margin: '0 0 48px' }}>We work with a diverse range of financial institutions and service providers.</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '20px' }}>
              {partnerTypes.map((pt) => (
                <div key={pt.title} style={{ background: '#f7f9fc', borderRadius: '16px', padding: '28px 24px', border: '1px solid #e8edf3', textAlign: 'center' }}>
                  <div style={{ fontSize: '2rem', marginBottom: '12px' }}>{pt.icon}</div>
                  <h3 style={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 700, fontSize: '0.95rem', color: '#1a2b4a', margin: '0 0 8px' }}>{pt.title}</h3>
                  <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.82rem', color: '#718096', margin: 0, lineHeight: 1.6 }}>{pt.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Benefits */}
        <div style={{ background: '#f7f9fc', padding: '80px 24px' }}>
          <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
            <h2 style={{ fontFamily: 'Merriweather, serif', fontWeight: 800, fontSize: '1.8rem', color: '#1a2b4a', textAlign: 'center', margin: '0 0 12px' }}>Why Partner with Lendo?</h2>
            <p style={{ fontFamily: 'DM Sans, sans-serif', color: '#718096', textAlign: 'center', fontSize: '0.95rem', margin: '0 0 48px' }}>Everything you need to grow your lending business.</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px' }}>
              {partnerBenefits.map((benefit) => (
                <div key={benefit.title} style={{ background: '#ffffff', borderRadius: '16px', padding: '28px', border: '1px solid #e8edf3', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                  <div style={{ fontSize: '1.8rem', marginBottom: '14px' }}>{benefit.icon}</div>
                  <h3 style={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 700, fontSize: '1rem', color: '#1a2b4a', margin: '0 0 8px' }}>{benefit.title}</h3>
                  <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.88rem', color: '#718096', margin: 0, lineHeight: 1.7 }}>{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Application Form */}
        <div style={{ background: '#ffffff', padding: '80px 24px' }}>
          <div style={{ maxWidth: '700px', margin: '0 auto' }}>
            <h2 style={{ fontFamily: 'Merriweather, serif', fontWeight: 800, fontSize: '1.8rem', color: '#1a2b4a', textAlign: 'center', margin: '0 0 12px' }}>Apply to Partner</h2>
            <p style={{ fontFamily: 'DM Sans, sans-serif', color: '#718096', textAlign: 'center', fontSize: '0.95rem', margin: '0 0 40px' }}>Fill out the form below and our partnerships team will reach out within 2 business days.</p>

            {submitted ? (
              <div style={{ textAlign: 'center', padding: '60px 40px', background: '#f0faf7', borderRadius: '20px', border: '1px solid #b2e8d8' }}>
                <div style={{ fontSize: '3rem', marginBottom: '16px' }}>🎉</div>
                <h3 style={{ fontFamily: 'Merriweather, serif', fontWeight: 700, fontSize: '1.4rem', color: '#1a2b4a', margin: '0 0 12px' }}>Application Received!</h3>
                <p style={{ fontFamily: 'DM Sans, sans-serif', color: '#4a5568', fontSize: '0.95rem', lineHeight: 1.6, margin: 0 }}>
                  Thank you for your interest in partnering with Lendo. Our partnerships team will review your application and reach out within 2 business days.
                </p>
              </div>
            ) : (
              <div style={{ background: '#f7f9fc', borderRadius: '20px', padding: '40px', border: '1px solid #e8edf3' }}>
                <form onSubmit={handleSubmit}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                    <div>
                      <label style={{ display: 'block', fontFamily: 'DM Sans, sans-serif', fontWeight: 600, fontSize: '0.85rem', color: '#2d3748', marginBottom: '8px' }}>Company Name *</label>
                      <input type="text" value={form.companyName} onChange={(e) => { setForm({ ...form, companyName: e.target.value }); if (errors.companyName) setErrors({ ...errors, companyName: '' }); }}
                        placeholder="Acme Lending Co." style={{ ...inputStyle, borderColor: errors.companyName ? '#e53e3e' : '#e2e8f0' }}
                        onFocus={(e) => (e.currentTarget.style.borderColor = '#00b28a')}
                        onBlur={(e) => (e.currentTarget.style.borderColor = errors.companyName ? '#e53e3e' : '#e2e8f0')} />
                      {errors.companyName && <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.75rem', color: '#e53e3e', marginTop: '4px', marginBottom: 0 }}>{errors.companyName}</p>}
                    </div>
                    <div>
                      <label style={{ display: 'block', fontFamily: 'DM Sans, sans-serif', fontWeight: 600, fontSize: '0.85rem', color: '#2d3748', marginBottom: '8px' }}>Contact Name *</label>
                      <input type="text" value={form.contactName} onChange={(e) => { setForm({ ...form, contactName: e.target.value }); if (errors.contactName) setErrors({ ...errors, contactName: '' }); }}
                        placeholder="John Smith" style={{ ...inputStyle, borderColor: errors.contactName ? '#e53e3e' : '#e2e8f0' }}
                        onFocus={(e) => (e.currentTarget.style.borderColor = '#00b28a')}
                        onBlur={(e) => (e.currentTarget.style.borderColor = errors.contactName ? '#e53e3e' : '#e2e8f0')} />
                      {errors.contactName && <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.75rem', color: '#e53e3e', marginTop: '4px', marginBottom: 0 }}>{errors.contactName}</p>}
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                    <div>
                      <label style={{ display: 'block', fontFamily: 'DM Sans, sans-serif', fontWeight: 600, fontSize: '0.85rem', color: '#2d3748', marginBottom: '8px' }}>Business Email *</label>
                      <input type="email" value={form.email} onChange={(e) => { setForm({ ...form, email: e.target.value }); if (errors.email) setErrors({ ...errors, email: '' }); }}
                        placeholder="john@company.com" style={{ ...inputStyle, borderColor: errors.email ? '#e53e3e' : '#e2e8f0' }}
                        onFocus={(e) => (e.currentTarget.style.borderColor = '#00b28a')}
                        onBlur={(e) => (e.currentTarget.style.borderColor = errors.email ? '#e53e3e' : '#e2e8f0')} />
                      {errors.email && <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.75rem', color: '#e53e3e', marginTop: '4px', marginBottom: 0 }}>{errors.email}</p>}
                    </div>
                    <div>
                      <label style={{ display: 'block', fontFamily: 'DM Sans, sans-serif', fontWeight: 600, fontSize: '0.85rem', color: '#2d3748', marginBottom: '8px' }}>Phone</label>
                      <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(555) 000-0000" style={inputStyle}
                        onFocus={(e) => (e.currentTarget.style.borderColor = '#00b28a')}
                        onBlur={(e) => (e.currentTarget.style.borderColor = '#e2e8f0')} />
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                    <div>
                      <label style={{ display: 'block', fontFamily: 'DM Sans, sans-serif', fontWeight: 600, fontSize: '0.85rem', color: '#2d3748', marginBottom: '8px' }}>Partner Type *</label>
                      <select value={form.partnerType} onChange={(e) => { setForm({ ...form, partnerType: e.target.value }); if (errors.partnerType) setErrors({ ...errors, partnerType: '' }); }}
                        style={{ ...inputStyle, borderColor: errors.partnerType ? '#e53e3e' : '#e2e8f0' }}
                        onFocus={(e) => (e.currentTarget.style.borderColor = '#00b28a')}
                        onBlur={(e) => (e.currentTarget.style.borderColor = errors.partnerType ? '#e53e3e' : '#e2e8f0')}>
                        <option value="">Select type</option>
                        <option>Bank or Credit Union</option>
                        <option>Alternative Lender</option>
                        <option>Referral Partner</option>
                        <option>Technology Partner</option>
                        <option>Other</option>
                      </select>
                      {errors.partnerType && <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.75rem', color: '#e53e3e', marginTop: '4px', marginBottom: 0 }}>{errors.partnerType}</p>}
                    </div>
                    <div>
                      <label style={{ display: 'block', fontFamily: 'DM Sans, sans-serif', fontWeight: 600, fontSize: '0.85rem', color: '#2d3748', marginBottom: '8px' }}>Website</label>
                      <input type="url" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://yourcompany.com" style={inputStyle}
                        onFocus={(e) => (e.currentTarget.style.borderColor = '#00b28a')}
                        onBlur={(e) => (e.currentTarget.style.borderColor = '#e2e8f0')} />
                    </div>
                  </div>
                  <div style={{ marginBottom: '24px' }}>
                    <label style={{ display: 'block', fontFamily: 'DM Sans, sans-serif', fontWeight: 600, fontSize: '0.85rem', color: '#2d3748', marginBottom: '8px' }}>Tell us about your partnership interest *</label>
                    <textarea value={form.description} onChange={(e) => { setForm({ ...form, description: e.target.value }); if (errors.description) setErrors({ ...errors, description: '' }); }}
                      placeholder="Describe your company, the types of loans you offer, and how you'd like to partner with Lendo…" rows={4}
                      style={{ ...inputStyle, resize: 'vertical', minHeight: '100px', borderColor: errors.description ? '#e53e3e' : '#e2e8f0' }}
                      onFocus={(e) => (e.currentTarget.style.borderColor = '#00b28a')}
                      onBlur={(e) => (e.currentTarget.style.borderColor = errors.description ? '#e53e3e' : '#e2e8f0')} />
                    {errors.description && <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.75rem', color: '#e53e3e', marginTop: '4px', marginBottom: 0 }}>{errors.description}</p>}
                  </div>
                  <button type="submit" disabled={submitting}
                    style={{ width: '100%', padding: '14px', background: submitting ? '#a0d4c8' : '#00b28a', color: '#ffffff', border: 'none', borderRadius: '10px', fontFamily: 'DM Sans, sans-serif', fontWeight: 700, fontSize: '1rem', cursor: submitting ? 'not-allowed' : 'pointer', transition: 'background 0.2s' }}
                    onMouseEnter={(e) => { if (!submitting) e.currentTarget.style.background = '#009970'; }}
                    onMouseLeave={(e) => { if (!submitting) e.currentTarget.style.background = '#00b28a'; }}>
                    {submitting ? 'Submitting…' : 'Submit Partnership Application'}
                  </button>
                </form>
              </div>
            )}
          </div>
        </div>

        <CTABanner />
      </main>
      <Footer />
      <style>{`
        @keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        @media (max-width: 768px) {
          .partner-grid { grid-template-columns: 1fr 1fr !important; }
          .benefits-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </>
  );
}
